import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaArrowRight, FaBook, FaCheckCircle, 
  FaClock, FaChartLine, FaLightbulb, FaQuoteLeft,
  FaPlay, FaPause, FaRedo, FaHome, FaEuroSign, FaNewspaper,
  FaShare, FaTwitter, FaLinkedin, FaTrophy
} from 'react-icons/fa';
import { GLOBE_TROTTER_CHARACTERS, getRandomQuote } from '../config/schools';

// Données du cours - 5 chapitres
const COURSE_DATA = {
  title: "Économie des Médias",
  professor: GLOBE_TROTTER_CHARACTERS?.seraphin,
  epochs: [
    {
      id: 1,
      title: "Les modèles économiques historiques",
      subtitle: "Comment la presse s'est financée",
      color: "#1B4965",
      icon: "📰",
      timeline: [
        { year: "1836", event: "Girardin invente la publicité dans la presse" },
        { year: "1863", event: "Le Petit Journal à 5 centimes" },
        { year: "1920s", event: "Âge d'or de la presse écrite" },
        { year: "1970s", event: "Arrivée de la télévision commerciale" }
      ],
      lessons: [
        {
          title: "Le modèle historique : la vente au numéro",
          points: [
            "Le lecteur paie directement pour l'information",
            "Prix = coûts de production + marge",
            "Relation directe entre qualité et revenus"
          ],
          keyFact: "Au XIXe siècle, un journal coûtait le prix d'un repas !"
        },
        {
          title: "La révolution publicitaire de 1836",
          points: [
            "Émile de Girardin divise par 2 le prix du journal",
            "La publicité compense la baisse du prix de vente",
            "Naissance du modèle à deux faces : lecteurs + annonceurs"
          ],
          keyFact: "La publicité a démocratisé l'accès à l'information !"
        },
        {
          title: "Le modèle mixte traditionnel",
          points: [
            "Revenus = Ventes + Publicité + Abonnements",
            "Équilibre délicat entre les trois sources",
            "La publicité représente 50 à 70% des revenus"
          ],
          keyFact: "Sans pub, votre journal coûterait 3 fois plus cher !"
        }
      ],
      quiz: {
        question: "Qui a inventé le modèle publicitaire dans la presse ?",
        options: ["Théophraste Renaudot", "Émile de Girardin", "Rupert Murdoch", "Jeff Bezos"],
        correct: 1
      }
    },
    {
      id: 2,
      title: "La crise de la presse écrite",
      subtitle: "Internet bouleverse tout",
      color: "#E63946",
      icon: "📉",
      timeline: [
        { year: "2000", event: "Éclatement de la bulle Internet" },
        { year: "2006", event: "Google News agrège les contenus" },
        { year: "2008", event: "Crise financière + crise des médias" },
        { year: "2012", event: "Newsweek arrête le papier" }
      ],
      lessons: [
        {
          title: "L'effondrement des petites annonces",
          points: [
            "Leboncoin, Monster, Meetic captent les annonces",
            "Perte de 50% des revenus pub en 10 ans",
            "Les journaux perdent leur vache à lait"
          ],
          keyFact: "Les petites annonces représentaient 40% des revenus !"
        },
        {
          title: "La gratuité de l'info sur le web",
          points: [
            "Pourquoi payer si c'est gratuit ailleurs ?",
            "Les agrégateurs (Google, Facebook) captent le trafic",
            "Le cercle vicieux : moins de revenus = moins de qualité"
          ],
          keyFact: "Google gagne plus en pub que toute la presse française !"
        },
        {
          title: "La chute des ventes papier",
          points: [
            "Division par 2 du tirage en 20 ans",
            "Les jeunes ne lisent plus le papier",
            "Coûts fixes élevés (impression, distribution)"
          ],
          keyFact: "60% des coûts d'un journal = papier + distribution !"
        }
      ],
      quiz: {
        question: "Quel service a le plus impacté les revenus des journaux ?",
        options: ["Netflix", "Les petites annonces en ligne", "Spotify", "Amazon Prime"],
        correct: 1
      }
    },
    {
      id: 3,
      title: "Les nouveaux modèles numériques",
      subtitle: "Réinventer le business model",
      color: "#2ECC71",
      icon: "💻",
      timeline: [
        { year: "2007", event: "Mediapart lance le paywall intégral" },
        { year: "2011", event: "New York Times lance le metered paywall" },
        { year: "2015", event: "Explosion des newsletters payantes" },
        { year: "2020", event: "Boom des abonnements (Covid)" }
      ],
      lessons: [
        {
          title: "Le paywall : faire payer le numérique",
          points: [
            "Paywall dur : tout est payant (Mediapart)",
            "Paywall souple : X articles gratuits/mois (Le Monde)",
            "Freemium : contenus premium réservés aux abonnés"
          ],
          keyFact: "Mediapart a 220 000 abonnés sans aucune publicité !"
        },
        {
          title: "La diversification des revenus",
          points: [
            "Événements et conférences payantes",
            "Formation et e-learning",
            "Consulting et études de marché"
          ],
          keyFact: "The Economist gagne plus avec les conférences qu'avec le journal !"
        },
        {
          title: "Les modèles alternatifs",
          points: [
            "Financement participatif (crowdfunding)",
            "Dons des lecteurs (The Guardian)",
            "Mécénat et fondations (ProPublica)"
          ],
          keyFact: "The Guardian a convaincu 1 million de lecteurs de donner !"
        }
      ],
      quiz: {
        question: "Quel média français fonctionne sans aucune publicité ?",
        options: ["Le Monde", "Le Figaro", "Mediapart", "20 Minutes"],
        correct: 2
      }
    },
    {
      id: 4,
      title: "Les géants du numérique",
      subtitle: "GAFAM vs Médias",
      color: "#9B59B6",
      icon: "🏢",
      timeline: [
        { year: "2004", event: "Facebook devient viral" },
        { year: "2007", event: "iPhone révolutionne la consommation" },
        { year: "2019", event: "Directive Copyright européenne" },
        { year: "2021", event: "Australie force Google à payer" }
      ],
      lessons: [
        {
          title: "Le duopole publicitaire Google-Facebook",
          points: [
            "70% de la pub numérique captée par 2 acteurs",
            "Les médias récupèrent les miettes",
            "Ciblage publicitaire impossible à égaler"
          ],
          keyFact: "Google et Facebook captent 1€ sur 5 dépensés en pub en France !"
        },
        {
          title: "Les droits voisins : la riposte",
          points: [
            "L'Europe impose le paiement des snippets",
            "Google et Facebook doivent payer les éditeurs",
            "Premiers accords signés en 2021"
          ],
          keyFact: "Google a versé 76 millions € à la presse française en 2022 !"
        },
        {
          title: "Dépendance aux plateformes",
          points: [
            "Les réseaux sociaux décident de la visibilité",
            "Un changement d'algorithme peut tuer un média",
            "Nécessité de construire sa propre audience"
          ],
          keyFact: "Un média peut perdre 50% de son trafic en une nuit !"
        }
      ],
      quiz: {
        question: "Quelle part de la pub numérique est captée par Google et Facebook ?",
        options: ["30%", "50%", "70%", "90%"],
        correct: 2
      }
    },
    {
      id: 5,
      title: "L'avenir des médias",
      subtitle: "Tendances et innovations",
      color: "#FFB703",
      icon: "🚀",
      timeline: [
        { year: "2022", event: "ChatGPT bouleverse la création de contenu" },
        { year: "2023", event: "Substack atteint 2M d'abonnés payants" },
        { year: "2024", event: "L'IA génère des articles" },
        { year: "2025", event: "Régulation de l'IA et des médias" }
      ],
      lessons: [
        {
          title: "L'économie de la créateur",
          points: [
            "Les journalistes deviennent des marques",
            "Newsletters individuelles (Substack)",
            "Podcasts, YouTube : nouveaux formats"
          ],
          keyFact: "Certains journalistes Substack gagnent plus d'1 million $/an !"
        },
        {
          title: "L'intelligence artificielle",
          points: [
            "Automatisation des articles simples",
            "Personnalisation des contenus",
            "Risques : désinformation, plagiat, emplois"
          ],
          keyFact: "80% des articles sportifs sont déjà écrits par des robots !"
        },
        {
          title: "Vers un journalisme durable",
          points: [
            "Qualité plutôt que quantité",
            "Relation directe avec les lecteurs",
            "Transparence sur le financement"
          ],
          keyFact: "Les lecteurs paient plus quand ils font confiance !"
        }
      ],
      quiz: {
        question: "Quelle plateforme permet aux journalistes de vendre des newsletters ?",
        options: ["WordPress", "Medium", "Substack", "Blogger"],
        correct: 2
      }
    }
  ]
};

// Composant Badge partageable
function ShareableBadge({ courseName, completedEpochs, totalEpochs, onClose }) {
  const percentage = Math.round((completedEpochs / totalEpochs) * 100);
  
  const shareOnTwitter = () => {
    const text = `J'ai complété ${percentage}% du cours "${courseName}" sur @DailyGlobeSchool ! #Journalisme #Formation`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };
  
  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}`, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.5, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        className="bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 p-1 rounded-2xl max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-white rounded-2xl p-6 text-center">
          <motion.div
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-6xl mb-4"
          >
            <FaTrophy className="mx-auto text-yellow-500" />
          </motion.div>
          
          <h2 className="comic-title text-2xl text-gray-800 mb-2">
            Certificat de Réussite
          </h2>
          
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2 px-4 rounded-lg mb-4">
            <p className="text-lg font-bold">{courseName}</p>
          </div>
          
          <div className="flex justify-center items-center gap-2 mb-4">
            <div className="text-4xl font-bold text-green-500">{percentage}%</div>
            <div className="text-gray-500 text-sm text-left">
              <p>{completedEpochs}/{totalEpochs} chapitres</p>
              <p>complétés</p>
            </div>
          </div>
          
          <div className="flex justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`text-2xl ${i < Math.ceil(completedEpochs * 5 / totalEpochs) ? '' : 'grayscale opacity-30'}`}
              >
                ⭐
              </motion.span>
            ))}
          </div>
          
          <p className="text-sm text-gray-500 mb-4">
            DailyGlobe School - {new Date().toLocaleDateString('fr-FR')}
          </p>
          
          <div className="border-t pt-4">
            <p className="text-sm text-gray-600 mb-3">Partager mon badge :</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={shareOnTwitter}
                className="flex items-center gap-2 bg-blue-400 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <FaTwitter /> Twitter
              </button>
              <button
                onClick={shareOnLinkedIn}
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <FaLinkedin /> LinkedIn
              </button>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="mt-4 text-gray-500 hover:text-gray-700 text-sm"
          >
            Fermer
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Fonction pour mélanger un tableau (Fisher-Yates shuffle)
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function MediaEconomicsPage() {
  const navigate = useNavigate();
  const [currentEpoch, setCurrentEpoch] = useState(0);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [currentPoint, setCurrentPoint] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswer, setQuizAnswer] = useState(null);
  const [completedEpochs, setCompletedEpochs] = useState([]);
  const [showTimeline, setShowTimeline] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  const [professorQuote, setProfessorQuote] = useState("J'ai justement ce qu'il vous faut !");
  const [showBadge, setShowBadge] = useState(false);
  const [shuffledQuiz, setShuffledQuiz] = useState({ options: [], correctIndex: 0 });

  const epoch = COURSE_DATA.epochs[currentEpoch];
  const lesson = epoch?.lessons[currentLesson];
  const totalLessons = epoch?.lessons.length || 0;
  const totalPoints = lesson?.points.length || 0;

  // Charger la progression sauvegardée
  useEffect(() => {
    const saved = localStorage.getItem('mediaEconomicsProgress');
    if (saved) {
      const progress = JSON.parse(saved);
      setCurrentEpoch(progress.epoch || 0);
      setCurrentLesson(progress.lesson || 0);
      setCurrentPoint(progress.point || 0);
      setCompletedEpochs(progress.completed || []);
    }
    if (COURSE_DATA.professor && getRandomQuote) {
      setProfessorQuote(getRandomQuote(COURSE_DATA.professor) || "J'ai justement ce qu'il vous faut !");
    }
  }, []);

  // Sauvegarder la progression
  useEffect(() => {
    localStorage.setItem('mediaEconomicsProgress', JSON.stringify({
      epoch: currentEpoch,
      lesson: currentLesson,
      point: currentPoint,
      completed: completedEpochs
    }));
  }, [currentEpoch, currentLesson, currentPoint, completedEpochs]);

  // Auto-play
  useEffect(() => {
    if (autoPlay && !showQuiz) {
      const timer = setTimeout(() => {
        handleNext();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [autoPlay, currentPoint, currentLesson, currentEpoch, showQuiz]);

  const handleNext = () => {
    if (currentPoint < totalPoints - 1) {
      setCurrentPoint(currentPoint + 1);
    } else if (currentLesson < totalLessons - 1) {
      setCurrentLesson(currentLesson + 1);
      setCurrentPoint(0);
    } else {
      // Mélanger les réponses du quiz avant de l'afficher
      const quiz = epoch.quiz;
      const optionsWithIndex = quiz.options.map((opt, idx) => ({ text: opt, originalIndex: idx }));
      const shuffled = shuffleArray(optionsWithIndex);
      const newCorrectIndex = shuffled.findIndex(opt => opt.originalIndex === quiz.correct);
      setShuffledQuiz({
        options: shuffled.map(opt => opt.text),
        correctIndex: newCorrectIndex
      });
      setShowQuiz(true);
      setAutoPlay(false);
    }
  };

  const handlePrev = () => {
    if (currentPoint > 0) {
      setCurrentPoint(currentPoint - 1);
    } else if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
      setCurrentPoint(epoch.lessons[currentLesson - 1].points.length - 1);
    } else if (currentEpoch > 0) {
      const prevEpoch = COURSE_DATA.epochs[currentEpoch - 1];
      setCurrentEpoch(currentEpoch - 1);
      setCurrentLesson(prevEpoch.lessons.length - 1);
      setCurrentPoint(prevEpoch.lessons[prevEpoch.lessons.length - 1].points.length - 1);
      setShowQuiz(false);
    }
  };

  const handleQuizAnswer = (index) => {
    setQuizAnswer(index);
    if (index === shuffledQuiz.correctIndex) {
      if (!completedEpochs.includes(currentEpoch)) {
        setCompletedEpochs([...completedEpochs, currentEpoch]);
      }
      setTimeout(() => {
        if (currentEpoch < COURSE_DATA.epochs.length - 1) {
          setCurrentEpoch(currentEpoch + 1);
          setCurrentLesson(0);
          setCurrentPoint(0);
          setShowQuiz(false);
          setQuizAnswer(null);
        }
      }, 2000);
    }
  };

  const goToEpoch = (index) => {
    setCurrentEpoch(index);
    setCurrentLesson(0);
    setCurrentPoint(0);
    setShowQuiz(false);
    setQuizAnswer(null);
    setShowTimeline(false);
  };

  const resetProgress = () => {
    setCurrentEpoch(0);
    setCurrentLesson(0);
    setCurrentPoint(0);
    setCompletedEpochs([]);
    setShowQuiz(false);
    setQuizAnswer(null);
    localStorage.removeItem('mediaEconomicsProgress');
  };

  return (
    <div className="min-h-screen" style={{ background: '#1a1a2e' }}>
      {/* Header */}
      <header className="border-b-4 border-black" style={{ background: '#16213e' }}>
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 text-white hover:text-yellow-400"
            data-testid="back-button"
          >
            <FaArrowLeft />
            <span className="comic-text">Retour</span>
          </button>
          <h1 className="comic-title text-xl text-white flex items-center">
            <FaChartLine className="mr-2" /> {COURSE_DATA.title}
          </h1>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowBadge(true)}
              className="comic-button bg-yellow-400 text-black text-sm px-3 py-1"
              title="Voir mon badge"
              data-testid="share-badge-btn"
            >
              <FaShare />
            </button>
            <span className="text-white/70 text-sm">
              {completedEpochs.length}/{COURSE_DATA.epochs.length}
            </span>
            <button
              onClick={() => setShowTimeline(!showTimeline)}
              className="comic-button bg-yellow-400 text-black text-sm px-3 py-1"
              data-testid="timeline-btn"
            >
              <FaClock />
            </button>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="h-2 bg-gray-700">
        <motion.div 
          className="h-full"
          style={{ background: epoch?.color }}
          initial={{ width: 0 }}
          animate={{ 
            width: `${((currentEpoch * 100 / COURSE_DATA.epochs.length) + 
              (currentLesson * 100 / COURSE_DATA.epochs.length / totalLessons) +
              (currentPoint * 100 / COURSE_DATA.epochs.length / totalLessons / Math.max(1, totalPoints)))}%` 
          }}
        />
      </div>

      {/* Timeline sidebar */}
      <AnimatePresence>
        {showTimeline && (
          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            className="fixed left-0 top-20 bottom-0 w-72 bg-gray-800 border-r-4 border-black z-50 overflow-y-auto"
          >
            <div className="p-4">
              <h3 className="comic-title text-white mb-4">Chapitres</h3>
              {COURSE_DATA.epochs.map((ep, idx) => (
                <button
                  key={idx}
                  onClick={() => goToEpoch(idx)}
                  className={`w-full text-left p-3 mb-2 rounded-lg border-2 transition-all ${
                    idx === currentEpoch 
                      ? 'border-yellow-400 bg-yellow-400/20' 
                      : 'border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="text-2xl mr-2">{ep.icon}</span>
                    <div className="flex-1">
                      <p className="comic-text text-white text-sm">{ep.title}</p>
                      <p className="text-xs text-gray-400">{ep.subtitle}</p>
                    </div>
                    {completedEpochs.includes(idx) && (
                      <FaCheckCircle className="text-green-400" />
                    )}
                  </div>
                </button>
              ))}
              <button
                onClick={resetProgress}
                className="w-full mt-4 comic-button bg-red-500 text-white text-sm"
              >
                <FaRedo className="mr-1" /> Recommencer
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Badge modal */}
      <AnimatePresence>
        {showBadge && (
          <ShareableBadge
            courseName={COURSE_DATA.title}
            completedEpochs={completedEpochs.length}
            totalEpochs={COURSE_DATA.epochs.length}
            onClose={() => setShowBadge(false)}
          />
        )}
      </AnimatePresence>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Professor */}
          <div className="lg:col-span-1">
            <motion.div 
              className="comic-panel text-center sticky top-4"
              style={{ background: '#F8F4E3' }}
            >
              <img 
                src={COURSE_DATA.professor?.image} 
                alt={COURSE_DATA.professor?.name || 'Séraphin'}
                className="w-20 h-20 mx-auto mb-3 object-contain"
              />
              <h3 className="comic-text text-lg" style={{ color: '#1B4965' }}>
                {COURSE_DATA.professor?.name || 'Séraphin'}
              </h3>
              <p className="text-xs text-gray-500 mb-2">Coach Business</p>
              <div className="speech-bubble text-sm">
                <FaQuoteLeft className="text-gray-300 mb-1" />
                <p className="italic">"{professorQuote}"</p>
              </div>
            </motion.div>

            {/* Mini timeline */}
            <motion.div 
              className="comic-panel mt-4 hidden lg:block"
              style={{ background: epoch?.color + '20' }}
            >
              <h4 className="comic-text text-sm mb-2" style={{ color: epoch?.color }}>
                Dates clés
              </h4>
              <div className="space-y-2">
                {epoch?.timeline.map((item, idx) => (
                  <div key={idx} className="flex items-start text-xs">
                    <span className="font-bold mr-2" style={{ color: epoch?.color }}>
                      {item.year}
                    </span>
                    <span className="text-gray-600">{item.event}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Main content */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {!showQuiz ? (
                <motion.div
                  key={`${currentEpoch}-${currentLesson}-${currentPoint}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="relative"
                >
                  {/* Modern card design */}
                  <div 
                    className="rounded-2xl overflow-hidden"
                    style={{ 
                      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                      border: `3px solid ${epoch?.color}`
                    }}
                  >
                    {/* Header with gradient */}
                    <div 
                      className="p-4"
                      style={{ background: `linear-gradient(135deg, ${epoch?.color}40 0%, ${epoch?.color}20 100%)` }}
                    >
                      <div className="flex justify-between items-start">
                        <div 
                          className="inline-flex items-center px-4 py-2 rounded-full"
                          style={{ background: epoch?.color }}
                        >
                          <span className="text-2xl mr-2">{epoch?.icon}</span>
                          <span className="comic-text text-white">{epoch?.title}</span>
                        </div>
                        <div className="text-white/50 text-sm">
                          Leçon {currentLesson + 1}/{totalLessons}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 min-h-[350px]">
                      <motion.h2 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-2xl md:text-3xl mb-6 text-white"
                        style={{ fontFamily: 'Bangers, cursive' }}
                      >
                        {lesson?.title}
                      </motion.h2>

                      <div className="space-y-4">
                        {lesson?.points.slice(0, currentPoint + 1).map((point, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx === currentPoint ? 0.3 : 0 }}
                            className="flex items-start bg-white/5 rounded-lg p-3"
                          >
                            <span 
                              className="text-2xl mr-3 mt-1"
                              style={{ color: epoch?.color }}
                            >
                              <FaEuroSign />
                            </span>
                            <p className="text-lg text-white/90">
                              {point}
                            </p>
                          </motion.div>
                        ))}
                      </div>

                      {/* Key fact */}
                      {currentPoint === totalPoints - 1 && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5 }}
                          className="mt-8 p-4 rounded-xl"
                          style={{ 
                            background: `linear-gradient(135deg, ${epoch?.color}30 0%, ${epoch?.color}10 100%)`,
                            border: `2px dashed ${epoch?.color}`
                          }}
                        >
                          <div className="flex items-center">
                            <FaLightbulb className="text-3xl mr-3" style={{ color: epoch?.color }} />
                            <p className="text-lg text-white">
                              {lesson?.keyFact}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </div>

                  {/* Navigation */}
                  <div className="flex justify-between items-center mt-4">
                    <button
                      onClick={handlePrev}
                      disabled={currentEpoch === 0 && currentLesson === 0 && currentPoint === 0}
                      className="comic-button bg-gray-600 text-white disabled:opacity-50"
                      data-testid="prev-btn"
                    >
                      <FaArrowLeft className="mr-2" /> Précédent
                    </button>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setAutoPlay(!autoPlay)}
                        className={`comic-button ${autoPlay ? 'bg-red-500' : 'bg-green-500'} text-white`}
                        data-testid="autoplay-btn"
                      >
                        {autoPlay ? <FaPause /> : <FaPlay />}
                      </button>
                      <span className="text-white/70 text-sm">
                        Point {currentPoint + 1}/{totalPoints}
                      </span>
                    </div>

                    <button
                      onClick={handleNext}
                      className="comic-button text-white"
                      style={{ background: epoch?.color }}
                      data-testid="next-btn"
                    >
                      Suivant <FaArrowRight className="ml-2" />
                    </button>
                  </div>
                </motion.div>
              ) : (
                /* Quiz */
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="comic-panel"
                  style={{ background: '#F8F4E3' }}
                  data-testid="quiz-panel"
                >
                  <div className="text-center mb-6">
                    <span className="text-4xl">{epoch?.icon}</span>
                    <h2 className="comic-title text-2xl mt-2" style={{ color: epoch?.color }}>
                      Quiz - {epoch?.title}
                    </h2>
                  </div>

                  <div className="max-w-xl mx-auto">
                    <p className="comic-text text-xl text-center mb-6">
                      {epoch?.quiz.question}
                    </p>

                    <div className="space-y-3">
                      {shuffledQuiz.options.map((option, idx) => (
                        <motion.button
                          key={idx}
                          whileHover={{ scale: quizAnswer === null ? 1.02 : 1 }}
                          whileTap={{ scale: quizAnswer === null ? 0.98 : 1 }}
                          onClick={() => quizAnswer === null && handleQuizAnswer(idx)}
                          disabled={quizAnswer !== null}
                          className={`w-full p-4 rounded-lg border-3 text-left transition-all ${
                            quizAnswer === null 
                              ? 'border-gray-300 hover:border-gray-500 bg-white' 
                              : idx === shuffledQuiz.correctIndex
                                ? 'border-green-500 bg-green-100'
                                : idx === quizAnswer
                                  ? 'border-red-500 bg-red-100'
                                  : 'border-gray-200 bg-gray-50 opacity-50'
                          }`}
                          data-testid={`quiz-option-${idx}`}
                        >
                          <span className="comic-text">
                            {String.fromCharCode(65 + idx)}. {option}
                          </span>
                          {quizAnswer !== null && idx === shuffledQuiz.correctIndex && (
                            <FaCheckCircle className="inline ml-2 text-green-500" />
                          )}
                        </motion.button>
                      ))}
                    </div>

                    {quizAnswer !== null && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`mt-6 p-4 rounded-lg text-center ${
                          quizAnswer === shuffledQuiz.correctIndex 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {quizAnswer === shuffledQuiz.correctIndex ? (
                          <>
                            <p className="comic-text text-xl">Bravo ! 🎉</p>
                            <p className="text-sm mt-1">Passage au chapitre suivant...</p>
                          </>
                        ) : (
                          <>
                            <p className="comic-text text-xl">Pas tout à fait... 🤔</p>
                            <button
                              onClick={() => setQuizAnswer(null)}
                              className="comic-button bg-red-500 text-white mt-2"
                            >
                              Réessayer
                            </button>
                          </>
                        )}
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Course completion */}
            {currentEpoch === COURSE_DATA.epochs.length - 1 && showQuiz && quizAnswer === shuffledQuiz.correctIndex && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-6 comic-panel text-center"
                style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' }}
              >
                <h2 className="comic-title text-3xl text-white mb-2">
                  Félicitations ! 🏆
                </h2>
                <p className="text-white text-lg mb-4">
                  Tu as terminé le cours "{COURSE_DATA.title}" !
                </p>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => setShowBadge(true)}
                    className="comic-button bg-white text-gray-800"
                  >
                    <FaShare className="mr-2" /> Partager mon badge
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="comic-button bg-green-500 text-white"
                  >
                    <FaHome className="mr-2" /> Retour au Dashboard
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MediaEconomicsPage;
